import React from "react";
import {
  createColumn,
  formatNumericCell,
  formatPriceCell,
  getCommonBodyCellProps,
  getCommonFooterCellProps,
  getCommonHeadCellProps
} from "./commonUtils";
import { formatNumber } from "../../../utils/dataCalculations";
import { makeDateFilterFn, smartNumberFilterFn } from "../smartFilters";

// The displayed "Trade Date" prefers the date encoded in CustomTradeID/
// original_trade_id over the raw archive_date field when both exist (they
// can disagree on legacy/migrated trades) — used by the column's Cell,
// sortingFn, AND export so all three always agree on the same value.
const getArchiveDateISO = (row) => {
  const customTradeId = row.CustomTradeID;
  const originalTradeId = row.original_trade_id;
  const idStr =
    customTradeId && String(customTradeId).length >= 8
      ? String(customTradeId)
      : originalTradeId && String(originalTradeId).length >= 8
        ? String(originalTradeId)
        : null;
  if (idStr) {
    return `${idStr.substring(0, 4)}-${idStr.substring(4, 6)}-${idStr.substring(6, 8)}`;
  }
  return row.archive_date || "";
};

// Column filters: the date filter must match the displayed date (see
// getArchiveDateISO), not the raw archive_date accessor value, which can be a
// day off. Number columns ignore commas, signs and currency. Every other
// column uses the table's default smart text filter.
const archiveDateFilterFn = makeDateFilterFn(getArchiveDateISO);
const NUMBER_FILTER_COLUMNS = ["FilledAmt", "TradeAvgPrice", "net_money"];

const withSmartFilters = (cols) =>
  cols.map((col) => {
    if (col.accessorKey === "archive_date") {
      return { ...col, filterFn: archiveDateFilterFn };
    }
    if (NUMBER_FILTER_COLUMNS.includes(col.accessorKey)) {
      return { ...col, filterFn: smartNumberFilterFn };
    }
    return col;
  });

// Summary columns - shown in collapsed view
const buildSummaryColumns = (theme, globalFilter) => {
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const textStr = String(text);
    const searchStr = String(searchTerm);
    const regex = new RegExp(`(${searchStr})`, "gi");
    const parts = textStr.split(regex);
    return parts?.map((part, index) =>
      part.toLowerCase() === searchStr.toLowerCase() ? (
        <span
          key={index}
          style={{
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.contrastText,
            padding: "1px 2px",
            borderRadius: theme.shape.borderRadius * 0.5,
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "archive_date",
      "Trade Date",
      120,
      "left",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const dateA = getArchiveDateISO(rowA.original).replace(/-/g, "");
            const dateB = getArchiveDateISO(rowB.original).replace(/-/g, "");
            return dateA.localeCompare(dateB);
          },
          Cell: ({ cell, row, table }) => {
            const dateStr = getArchiveDateISO(row.original) || cell.getValue();
            return highlightMatch(dateStr, table.options.state.globalFilter);
          },
          // Export must show the same value as the Cell above, not the raw
          // archive_date field — see getArchiveDateISO.
          getExportValue: (row) => getArchiveDateISO(row),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "Strategy1",
      "Strategy",
      180,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "Symbol",
      "Symbol",
      120,
      "left",
      {
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "Alias2",
      "Alias",
      120,
      "left",
      {
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan" && value !== ""
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "PosAction",
      "Action",
      100,
      "center",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value || "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "FilledAmt",
      "Quantity",
      110,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              (value) => formatNumber(value, 0),
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
          Footer: ({ table }) => {
            const rows = table.getFilteredRowModel().rows;
            const total = rows.reduce((sum, row) => {
              const value = row.getValue("FilledAmt");
              return sum + (value ? parseFloat(value) : 0);
            }, 0);
            return (
              <div style={{ fontWeight: 700, textAlign: "right" }}>
                Sum: {formatNumber(total, 0)}
              </div>
            );
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "TradeAvgPrice",
      "Price",
      110,
      "right",
      {
        other: {
          Cell: ({ cell, row }) => {
            const rawValue = cell.getValue();
            if (rawValue == null || rawValue === "") return "-";

            const value = parseFloat(rawValue);
            if (Number.isNaN(value)) return "-";

            const action = row.original?.PosAction || "";
            const quantity = row.getValue("FilledAmt");
            const isNegativeAction = ["short", "sell"].includes(
              String(action).toLowerCase(),
            );
            const isNegativeQuantity =
              quantity != null && parseFloat(quantity) < 0;
            const shouldShowNegative =
              isNegativeAction || isNegativeQuantity || value < 0;

            const formattedValue = new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(Math.abs(value));

            return (
              <span
                style={{
                  color: shouldShowNegative
                    ? theme.palette.error.main
                    : theme.palette.text.primary,
                  fontWeight: shouldShowNegative ? 600 : 400,
                }}
              >
                {shouldShowNegative ? `(${formattedValue})` : formattedValue}
              </span>
            );
          },
          Footer: ({ table }) => {
            const rows = table.getFilteredRowModel().rows;
            // Calculate weighted average: Sum(Price * Quantity) / Sum(Quantity)
            let totalWeightedPrice = 0;
            let totalQuantity = 0;

            rows.forEach((row) => {
              const price = row.getValue("TradeAvgPrice");
              const quantity = row.getValue("FilledAmt");
              if (price && quantity) {
                const action = row.original?.PosAction || "";
                const isNegativeAction = ["short", "sell"].includes(
                  String(action).toLowerCase(),
                );
                const numericQuantity = parseFloat(quantity);
                const signedQuantity = isNegativeAction
                  ? -Math.abs(numericQuantity)
                  : numericQuantity;

                totalWeightedPrice += parseFloat(price) * signedQuantity;
                totalQuantity += signedQuantity;
              }
            });

            const weightedAvg =
              totalQuantity !== 0 ? totalWeightedPrice / totalQuantity : 0;
            const isNegativeAvg = weightedAvg < 0;
            const formattedAvg = new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(Math.abs(weightedAvg));
            return (
              <div
                style={{
                  fontWeight: 700,
                  textAlign: "right",
                  color: isNegativeAvg
                    ? theme.palette.error.main
                    : theme.palette.text.primary,
                }}
              >
                Avg: {isNegativeAvg ? `(${formattedAvg})` : formattedAvg}
              </div>
            );
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_money",
      "Net Money",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value != null
              ? new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(value)
              : "-";
          },
          Footer: ({ table }) => {
            const rows = table.getFilteredRowModel().rows;
            const total = rows.reduce((sum, row) => {
              const value = row.getValue("net_money");
              return sum + (value ? parseFloat(value) : 0);
            }, 0);
            return (
              <div style={{ fontWeight: 700, textAlign: "right" }}>
                Sum:{" "}
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(total)}
              </div>
            );
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "BrokerCode",
      "Broker",
      100,
      "left",
      {
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      },
    ),
  ];
};

// Full columns configuration (for reference/export)
const buildColumns = (theme, globalFilter) => {
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const textStr = String(text);
    const searchStr = String(searchTerm);
    const regex = new RegExp(`(${searchStr})`, "gi");
    const parts = textStr.split(regex);
    return parts?.map((part, index) =>
      part.toLowerCase() === searchStr.toLowerCase() ? (
        <span
          key={index}
          style={{
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.contrastText,
            padding: "1px 2px",
            borderRadius: theme.shape.borderRadius * 0.5,
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "archive_date",
      "Trade Date",
      120,
      "left",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            // Helper function to extract date from trade ID
            const getDateFromRow = (row) => {
              const customTradeId = row.original.CustomTradeID;
              const originalTradeId = row.original.original_trade_id;
              const archiveDate = row.original.archive_date;

              // Try CustomTradeID first (first 8 chars)
              if (customTradeId && String(customTradeId).length >= 8) {
                return String(customTradeId).substring(0, 8);
              }
              // Try original_trade_id
              if (originalTradeId && String(originalTradeId).length >= 8) {
                return String(originalTradeId).substring(0, 8);
              }
              // Fallback to archive_date
              if (archiveDate) {
                return archiveDate.replace(/-/g, "");
              }
              return "";
            };

            const dateA = getDateFromRow(rowA);
            const dateB = getDateFromRow(rowB);

            return dateA.localeCompare(dateB);
          },
          Cell: ({ cell, row, table }) => {
            // Try to parse date from CustomTradeID or original_trade_id first
            const customTradeId = row.original.CustomTradeID;
            const originalTradeId = row.original.original_trade_id;

            let dateStr = cell.getValue();

            // Parse from CustomTradeID if available (first 8 chars in YYYYMMDD format)
            if (customTradeId && String(customTradeId).length >= 8) {
              const idStr = String(customTradeId);
              const year = idStr.substring(0, 4);
              const month = idStr.substring(4, 6);
              const day = idStr.substring(6, 8);
              dateStr = `${year}-${month}-${day}`;
            }
            // Otherwise try original_trade_id (first 8 digits)
            else if (originalTradeId && String(originalTradeId).length >= 8) {
              const idStr = String(originalTradeId);
              const year = idStr.substring(0, 4);
              const month = idStr.substring(4, 6);
              const day = idStr.substring(6, 8);
              dateStr = `${year}-${month}-${day}`;
            }

            return highlightMatch(dateStr, table.options.state.globalFilter);
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyType",
      "Strategy Type",
      120,
      "left",
      {
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan" && value !== ""
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "Strategy1",
      "Strategy Name",
      180,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "Symbol",
      "Symbol",
      120,
      "left",
      {
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "Isin",
      "ISIN",
      140,
      "left",
      {
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "PosAction",
      "Action",
      120,
      "center",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value || "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "FilledAmt",
      "Quantity",
      110,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "TradeAvgPrice",
      "Price",
      110,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "MV",
      "Market Value",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value != null
              ? new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(value)
              : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "SecType",
      "Security Type",
      130,
      "left",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value && value !== "nan" ? value : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sec_type2",
      "Sec Type 2",
      110,
      "left",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value && value !== "nan" ? value : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "settle_currency",
      "Settle Currency",
      130,
      "left",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value && value !== "nan" ? value : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "BrokerCode",
      "Broker",
      100,
      "left",
      {
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "CustomTradeID",
      "Trade ID",
      120,
      "left",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value && value !== "nan" ? value : "-";
          },
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "BB_Ticker_Code",
      "Bloomberg Ticker",
      150,
      "left",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value && value !== "nan" ? value : "-";
          },
        },
      },
    ),
  ];
};

export const summaryColumns = (theme, globalFilter) =>
  withSmartFilters(buildSummaryColumns(theme, globalFilter));

export const columns = (theme, globalFilter) =>
  withSmartFilters(buildColumns(theme, globalFilter));
